import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTheme, YStack } from 'tamagui';

import {
  CobuContainer,
  CobuHeader,
  CobuText,
  CobuDisabledOverlay,
  CobuLoadingOverlay,
  GroupDetailSkeleton,
  // CobuTabHeader,
} from '@cobu/ui';
import { GroupDetailHeader } from '../components/groups/GroupDetailHeader';
import { GroupDetailInfo } from '../components/groups/GroupDetailInfo';
import GroupDiscussion from '../components/chat/groupDiscussion/GroupDiscussion';
import { Strings } from '../constants';
import { findMarketplaceGroup } from '../constants/marketplace';
import { useOrganizedGroups } from '../hooks/groups/groupsHooks';
import { useGroupDetail } from '../hooks/groups/useGroupDetail';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { showErrorMessage } from '../utils/messageToast';
import { PullToRefreshWrapper } from '../components/ui/PullToRefreshWrapper';
import { useGroupDiscussion } from '../hooks/useGroupDiscussion';
import { CommentType } from '@cobu/types';
import { logger } from '../utils/logger';

export default function MarketplaceScreen() {
  const theme = useTheme();
  const strings = Strings.get();

  const { allGroups, isLoading: groupsLoading, error: groupsError } = useOrganizedGroups();
  const [marketplaceGroupId, setMarketplaceGroupId] = useState<string | null>(null);

  // Use group detail hook once we have the marketplace group ID
  const {
    // Core Data
    group,
    gatherings,
    members,
    userIsGroupMember,

    // Loading States
    isLoading: groupDetailLoading,
    error: groupDetailError,
    operationLoading,
    operationError,
    isJoining,
    isLeaving,

    // Permissions
    permissions,

    // Configurations
    groupDiscussionProps,

    // Enhanced Handlers
    handleJoinGroup,
    handleLeaveGroup,
    handleRetry,
    refetchGroup,
    handleInfoPress,
  } = useGroupDetail(marketplaceGroupId ?? '');

  // Use useGroupDiscussion hook for refresh functionality
  const { refreshComments, refreshing: commentsRefreshing } = useGroupDiscussion({
    groupId: marketplaceGroupId,
    commentType: CommentType.GroupComment,
  });

  // Combined refresh handler for marketplace (refreshes both group data and comments)
  const handleMarketplaceRefresh = useCallback(async () => {
    if (!marketplaceGroupId || !group) return;
    
    try {
      await Promise.all([
        refetchGroup(),
        refreshComments(),
      ]);
    } catch (error) {
      logger.error('Error refreshing marketplace:', error);
    }
  }, [marketplaceGroupId, group, refetchGroup, refreshComments]);

  // Combined refreshing state
  const isRefreshing = commentsRefreshing;

  // Shared header and info (mirroring groups/[id].tsx) - must be declared before any early returns
  const sharedHeaderAndInfo = useMemo(() => (
    group ? (
      <YStack>
        <GroupDetailHeader
          group={group}
          userIsGroupMember={userIsGroupMember}
          isJoining={isJoining || operationLoading}
          isLeaving={isLeaving || operationLoading}
          onJoinPress={handleJoinGroup}
          onLeavePress={handleLeaveGroup}
        />

        <GroupDetailInfo
          group={group}
          gatherings={gatherings}
          members={members}
          userIsGroupMember={userIsGroupMember}
          onRefresh={refetchGroup}
        />
      </YStack>
    ) : null
  ), [
    group,
    userIsGroupMember,
    isJoining,
    isLeaving,
    operationLoading,
    gatherings,
    members,
    handleJoinGroup,
    handleLeaveGroup,
    refetchGroup,
  ]);

  // useEffect(() => {
  //   // Track analytics when marketplace tab is viewed
  //   analyticsService.trackEvent(AnalyticsEvent.VIEW_GROUPS, {
  //     context: 'marketplace_tab',
  //     buildingId: user?.building?.uuid,
  //   });
  // }, [user?.building?.uuid]);

  useEffect(() => {
    if (groupsLoading || !allGroups) {
      return;
    }

    if (groupsError) {
      logger.error('Error loading groups for marketplace:', groupsError);
      return;
    }

    // Find the marketplace group using the same logic as the old app
    const marketplaceGroup = findMarketplaceGroup(allGroups);

    if (!marketplaceGroup) {
      logger.warn('No marketplace group found');
      return;
    }

    // Set the marketplace group ID to trigger group detail loading
    setMarketplaceGroupId(marketplaceGroup.uuid);

    // // Track marketplace group access
    /* ANALYTICS_DISABLED: VIEW_GROUP_DETAILS ('view_group_details')
      analyticsService.trackEvent(AnalyticsEvent.VIEW_GROUP_DETAILS, {
            groupId: marketplaceGroup.uuid,
            groupName: marketplaceGroup.name,
            source: 'marketplace_tab',
            isMarketplace: true,
          });
      */
  }, [allGroups, groupsLoading, groupsError]);

  // Error state
  const hasShownErrorRef = useRef(false);
  const hasError = Boolean(groupsError || groupDetailError);


  useEffect(() => {
    if (hasError && !hasShownErrorRef.current) {
      showErrorMessage(strings.common.somethingWentWrong);
      hasShownErrorRef.current = true;
    }
  }, [hasError]);

  // Handle loading states - only show skeleton on initial load, not during refresh
  if (groupsLoading || (groupDetailLoading && !group)) {
    return (
      <CobuContainer bg="$white">
        <GroupDetailSkeleton />
      </CobuContainer>
    );
  }

  if (hasError || !marketplaceGroupId) {
    return null;
  }

  if (!group) {
    return (
      <CobuContainer bg="$white">
        <GroupDetailSkeleton />
      </CobuContainer>
    );
  }

  return (
    <PullToRefreshWrapper
      onRefresh={handleMarketplaceRefresh}
      isRefreshing={isRefreshing}
      useWindowScroll={true}
    >
      <CobuContainer bg="$white" pos="relative">
        {/* Show group header and info for non-members who can't see discussions */}
        {!permissions.canViewDiscussion && sharedHeaderAndInfo}

        {/* Group Discussion - only show to members or building-wide groups */}
        {permissions.canViewDiscussion && (
          <GroupDiscussion
            {...groupDiscussionProps}
            header={sharedHeaderAndInfo}
          />
        )}

      {/* Disabled Overlay for Non-Members (excluding building-wide groups) */}
      {permissions.showDisabledOverlay && (
        <CobuDisabledOverlay
          onButtonPress={handleJoinGroup}
          buttonText={strings.groupDetail.actions.joinGroup}
          disabled={isJoining || operationLoading}
          loading={isJoining || operationLoading}
        />
      )}

        {/* Full-Screen Loading Overlay for Operations */}
        <CobuLoadingOverlay
          visible={operationLoading}
          text={isJoining || operationLoading ? strings.groupDetail.actions.joining : isLeaving ? strings.groupDetail.actions.leaving : undefined}
        />
        {/* <UniversalButtonContainer /> */}
      </CobuContainer>
    </PullToRefreshWrapper>
  );
} 